import React from 'react';
import { Link } from 'react-router-dom';

// Trophées palette (mêmes valeurs que HomePage et autres pages légales)
const NAVY = '#1B1E38';
const CYAN = '#39A8E5';
const BLUE = '#4A71E5';

// Static content extracted from
// "réglement officiel trophées de l'innovation .docx" (version mai 2026).

// Version du règlement publié : à incrémenter à chaque modification de fond
// (dernière : report de la clôture des candidatures au 11 septembre 2026).
const VERSION = 'Août 2026';

// §3 calendar table
const CALENDAR_ROWS = [
  ['Ouverture des candidatures', '11 mai 2026'],
  ['Clôture des candidatures', '11 septembre 2026 (report de la date initiale du 31 août 2026)'],
  ['Pré-sélection des dossiers', 'Septembre 2026 (date exacte communiquée aux candidats)'],
  ['Pré-auditions', 'Septembre / octobre 2026'],
  ['Audition des finalistes devant le jury', 'Mercredi 14 octobre 2026'],
  ['Annonce des lauréats / Cérémonie', 'Mercredi 18 novembre 2026 — Élysées Biarritz, 22-24 rue Quentin Bauchart, 75008 Paris'],
];

// End-of-document recap table
const RECAP_ROWS = [
  ['Organisateur', 'LBP AM (La Banque Postale Asset Management)'],
  ['Opérateur plateforme', 'Investance Partners — 31 rue Saint-Augustin, 75002 Paris'],
  ['Plateforme', 'https://isi-app.paradoxe.ai/', 'https://isi-app.paradoxe.ai/'],
  ['Ouverture candidatures', '11 mai 2026'],
  ['Clôture candidatures', '11 septembre 2026'],
  ['Audition jury', '14 octobre 2026'],
  ['Cérémonie lauréats', "18 novembre 2026 — Élysées Biarritz, Paris 8e"],
  ['Nombre de prix', '3 prix'],
  ['Référente RGPD', 'rgpd@investance-partners.com', 'mailto:rgpd@investance-partners.com'],
  ['Contact Concours', 'contact.lbpam@investance-partners.com', 'mailto:contact.lbpam@investance-partners.com'],
  ['Composition du jury', "Le jury 2026 n'est pas encore constitué"],
  ['Dotations 2026', "Les dotations 2026 ne sont pas encore déterminées"],
  ['Version', VERSION],
];

export default function ReglementPage() {
  return (
    <div style={{ minHeight: '100vh', background: 'white' }}>
      {/* ===== Top bar ===== */}
      <header style={{
        background: NAVY, color: 'white', padding: '14px 32px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        position: 'sticky', top: 0, zIndex: 100,
      }}>
        <Link to="/" style={{ display: 'flex', alignItems: 'baseline', gap: 8, textDecoration: 'none', color: 'white' }}>
          <span style={{ fontFamily: 'Lato, sans-serif', fontWeight: 800, fontSize: 18, letterSpacing: '-0.3px' }}>Plateforme ISI</span>
          <span style={{ fontFamily: 'Inter, sans-serif', fontWeight: 400, fontSize: 13, color: CYAN }}>by Paradoxe.ai for LBP AM</span>
        </Link>
        <nav style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <Link to="/faq" style={navLinkStyle}>FAQ</Link>
          <a href="mailto:contact.tropheeslbpam@investance-partners.com" style={navLinkStyle}>Nous contacter</a>
          <Link to="/login" style={navLinkStyle}>Se connecter</Link>
          <Link to="/register" style={{ ...navLinkStyle, color: 'white', background: BLUE, border: `1px solid ${BLUE}` }}>Créer un compte</Link>
        </nav>
      </header>

      {/* ===== Hero ===== */}
      <section style={{
        background: `linear-gradient(180deg, ${NAVY} 0%, #2A2E4E 100%)`,
        color: 'white', padding: '56px 32px 40px',
      }}>
        <div style={{ maxWidth: 960, margin: '0 auto' }}>
          <p style={{ fontSize: 13, color: CYAN, fontWeight: 600, letterSpacing: 1.2, marginBottom: 12, textTransform: 'uppercase' }}>
            Concours
          </p>
          <h1 style={{ fontFamily: 'Lato, sans-serif', fontSize: 38, fontWeight: 800, lineHeight: 1.15, marginBottom: 12, marginTop: 0 }}>
            Règlement officiel
          </h1>
          <p style={{ fontSize: 16, lineHeight: 1.55, color: 'rgba(255,255,255,0.85)', maxWidth: 720, marginBottom: 0 }}>
            Trophées de l'innovation 2026
            <br />
            <span style={{ fontSize: 13.5, color: 'rgba(255,255,255,0.65)' }}>
              Organisés par LBP AM — Plateforme opérée par Investance Partners. Version {VERSION}.
            </span>
          </p>
        </div>
      </section>

      {/* ===== Body ===== */}
      <section style={{ padding: '48px 32px 64px', background: '#FAFBFD' }}>
        <div style={{ maxWidth: 880, margin: '0 auto' }}>

          {/* Article 1 */}
          <Article id={1} title="Organisateurs">
            <P>
              Les Trophées de l'innovation 2026 (ci-après « le Concours ») sont organisés par <strong>LBP AM</strong>{' '}
              (La Banque Postale Asset Management), dans le cadre de sa démarche d'innovation et de finance durable
              (ci-après « l'Organisateur »).
            </P>
            <P>
              La plateforme de candidature <strong>Paradoxe.AI</strong>, accessible à l'adresse{' '}
              <a href="https://isi-app.paradoxe.ai/" target="_blank" rel="noopener noreferrer" className="ml-link">https://isi-app.paradoxe.ai/</a>,
              est éditée et opérée par <strong>Investance Partners</strong>, société anonyme au capital de 113 950 euros,
              dont le siège social est situé au 31 rue Saint-Augustin, 75002 Paris, immatriculée au RCS de Paris sous le
              numéro 435 257 175.
            </P>
          </Article>

          {/* Article 2 */}
          <Article id={2} title="Objet du Concours">
            <P>
              Les Trophées de l'innovation 2026 ont pour objet de révéler, valoriser et accompagner des startups
              françaises innovantes apportant des réponses concrètes aux transformations économiques, technologiques,
              environnementales et sociétales.
            </P>
            <P style={{ marginBottom: 4 }}>Pour l'édition 2026, une attention particulière est portée aux secteurs suivants :</P>
            <Bullets items={[
              'Souveraineté technologique',
              'Santé et Healthtech / Biotech',
              'Finance et Fintech',
              'Environnement et Greentech',
              'Réindustrialisation',
              'Accès aux services essentiels',
            ]} />
            <P><strong>Trois prix</strong> seront décernés à l'issue du Concours.</P>
          </Article>

          {/* Article 3 */}
          <Article id={3} title="Calendrier">
            <div style={{ overflowX: 'auto', margin: '12px 0 16px' }}>
              <table style={tableStyle}>
                <tbody>
                  {CALENDAR_ROWS.map((row, i) => (
                    <tr key={i} style={{ background: i % 2 ? '#F7F9FC' : 'white' }}>
                      <td style={{ ...tdStyle, fontWeight: 600, color: NAVY, whiteSpace: 'nowrap' }}>{row[0]}</td>
                      <td style={tdStyle}>{row[1]}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <P>
              Les finalistes seront prévenus au minimum une semaine avant la date d'audition. L'Organisateur se réserve
              le droit de modifier le calendrier en cas de nécessité ; les candidats inscrits en seront informés par
              e-mail dans les meilleurs délais.
            </P>
          </Article>

          {/* Article 4 */}
          <Article id={4} title="Conditions d'éligibilité">
            <SubTitle>4.1 Critères d'éligibilité</SubTitle>
            <P>Peuvent soumettre une candidature les entreprises répondant à l'ensemble des critères suivants, étant précisé que les conditions financières relèvent d'une règle alternative (OU) :</P>
            <Bullets items={[
              'Startup créée il y a moins de 5 ans à la date de clôture des candidatures ;',
              "Réaliser un chiffre d'affaires compris entre 0,5 M€ et 5 M€ OU, si une levée de fonds est réalisée ou prévue, une levée comprise entre 0,5 M€ et 10 M€ — au moins l'une de ces deux conditions financières suffit ;",
              'Siège social et activités de R&D situés en France ;',
              'Non détenue majoritairement par un grand groupe, une institution financière ou une société étrangère ;',
              "Solution contribuant aux grands enjeux contemporains définis à l'article 2.",
            ]} />

            <SubTitle>4.2 Types de projets recherchés</SubTitle>
            <P>Sont recherchés des projets innovants présentant :</P>
            <Bullets items={[
              'une proposition de valeur claire et différenciante ;',
              'un produit ou service identifiable et déployable ;',
              'un potentiel de croissance démontré ;',
              "une capacité de passage à l'échelle ;",
              'un impact économique, technologique, environnemental ou sociétal mesurable.',
            ]} />
            <P>Les projets ne sont pas limités au secteur de la finance : tous les secteurs listés à l'article 2 sont éligibles.</P>

            <SubTitle>4.3 Exclusions</SubTitle>
            <P>Sont exclus du Concours :</P>
            <Bullets items={[
              'les entreprises dont le siège social ou la R&D ne sont pas situés en France ;',
              'les entreprises majoritairement détenues par un grand groupe, une institution ou une société étrangère ;',
              'les filiales ou entités directement liées à LBP AM ou à Investance Partners ;',
              'les entreprises dont un représentant ou fondateur est membre du jury.',
            ]} />
            <P>Un candidat non retenu lors d'une édition précédente peut soumettre une nouvelle candidature lors de l'édition suivante.</P>
          </Article>

          {/* Article 5 */}
          <Article id={5} title="Modalités de candidature">
            <SubTitle>5.1 Inscription et création de compte</SubTitle>
            <P>
              La participation au Concours est <strong>gratuite</strong>. Pour soumettre une candidature, le
              représentant légal de l'entreprise ou un fondateur dûment habilité doit :
            </P>
            <Bullets items={[
              <>créer un compte sur la plateforme (<a href="https://isi-app.paradoxe.ai/" target="_blank" rel="noopener noreferrer" className="ml-link">https://isi-app.paradoxe.ai/</a>) en renseignant une adresse e-mail professionnelle valide et un mot de passe sécurisé ;</>,
              <>accepter les présentes CGU, la <Link to="/politique-confidentialite" className="ml-link">Politique de confidentialité</Link> et la notice d'information RGPD ;</>,
              "renseigner les informations relatives à l'entreprise de manière exacte, complète et sincère.",
            ]} />
            <P>Un seul compte peut être créé par entreprise candidate. Un seul dossier de candidature peut être déposé par entreprise.</P>

            <SubTitle>5.2 Constitution du dossier de candidature</SubTitle>
            <P>Le dossier de candidature doit couvrir l'ensemble des éléments suivants :</P>
            <Bullets items={[
              "Présentation de l'entreprise : dénomination, forme juridique, RCS/SIRET, adresse, date de création ;",
              'Description de la solution innovante et de sa proposition de valeur ;',
              'Analyse de marché et positionnement concurrentiel ;',
              'Modèle économique et stratégie de développement ;',
              'Niveau de maturité technologique (TRL) ;',
              "Indicateurs financiers : chiffre d'affaires, levées de fonds, principaux KPIs ;",
              "Présentation de l'équipe dirigeante et des fondateurs (CV) ;",
              'Organisation commerciale ;',
              "Contribution aux enjeux de l'édition 2026 définis à l'article 2 ;",
              'Données extra-financières : indicateurs ESG, impact environnemental et social.',
            ]} />

            <SubTitle>5.3 Soumission et modification du dossier</SubTitle>
            <P>
              Le dossier doit être soumis via la plateforme avant le <strong>11 septembre 2026</strong>, date de clôture
              (initialement fixée au 31 août 2026, reportée par l'organisateur).
              Toute candidature incomplète à cette date sera déclarée irrecevable.
            </P>
            <P>
              Une fois soumis, le dossier ne peut plus être modifié par le candidat. Il est donc fortement conseillé
              de vérifier l'ensemble des informations avant la validation définitive. En cas d'erreur avérée, le
              candidat peut contacter l'équipe organisatrice via la rubrique « Nous contacter » de la plateforme.
            </P>
            <P>
              Un accusé de réception est adressé par e-mail au représentant inscrit dans un délai raisonnable suivant
              la soumission.
            </P>
          </Article>

          {/* Article 6 */}
          <Article id={6} title="Processus de sélection">
            <SubTitle>6.1 Étapes du processus</SubTitle>
            <P>La sélection se déroule en sept étapes successives :</P>
            <Bullets items={[
              'Dépôt du dossier sur la plateforme Paradoxe.AI ;',
              "Analyse de l'éligibilité par l'équipe organisatrice d'Investance Partners ;",
              'Pré-sélection des dossiers retenus pour la suite du processus ;',
              'Pré-auditions avec les entreprises pré-sélectionnées ;',
              'Transmission des 30 dossiers finalistes à LBP AM et au jury ;',
              'Audition des finalistes devant le jury (14 octobre 2026) ;',
              'Désignation des lauréats et annonce officielle lors de la cérémonie (18 novembre 2026).',
            ]} />

            <SubTitle>6.2 Composition du jury</SubTitle>
            <P>
              Le jury est composé de professionnels reconnus issus de l'investissement, de la finance, de la
              technologie et de l'innovation : fonds de capital-risque, dirigeants tech, investisseurs
              institutionnels. La composition complète du jury 2026 sera publiée sur le site officiel de LBP AM.
            </P>
            <P>Chaque membre du jury signe un engagement de confidentialité avant tout accès aux dossiers de candidature.</P>

            <SubTitle>6.3 Critères d'évaluation</SubTitle>
            <P>Les dossiers sont évalués selon les critères suivants :</P>
            <Bullets items={[
              'Caractère innovant de la solution ;',
              'Solidité du modèle économique ;',
              'Potentiel de croissance et scalabilité ;',
              'Maturité du produit ou service ;',
              "Qualité et complémentarité de l'équipe ;",
              "Capacité de passage à l'échelle ;",
              'Impact environnemental, social ou sociétal ;',
              "Contribution aux enjeux prioritaires de l'édition 2026.",
            ]} />

            <SubTitle>6.4 Souveraineté du jury</SubTitle>
            <Callout small>
              Le jury délibère de manière souveraine. Ses décisions sont prises à la majorité et sont définitives.
              Elles ne peuvent faire l'objet d'aucun recours. L'Organisateur ne communique pas les motifs individuels
              de non-sélection.
            </Callout>
          </Article>

          {/* Article 7 */}
          <Article id={7} title="Prix et dotations">
            <P><strong>Trois prix distincts</strong> sont décernés lors de la cérémonie du 18 novembre 2026.</P>
            <P>
              Les prix sont nominatifs et ne peuvent être ni cédés, ni échangés contre une contrepartie en numéraire,
              sauf disposition contraire expressément mentionnée par l'Organisateur.
            </P>
            <P>
              L'attribution d'un prix est conditionnée à la vérification de l'éligibilité du lauréat à la date de
              remise. En cas de découverte d'une inéligibilité postérieure à la désignation, l'Organisateur se réserve
              le droit d'annuler l'attribution du prix.
            </P>
          </Article>

          {/* Article 8 */}
          <Article id={8} title="Rôle de LBP AM dans le processus de sélection">
            <P>
              LBP AM intervient en qualité d'organisateur du Concours et de membre du jury lors de la phase d'audition
              finale. Les <strong>30 dossiers pré-sélectionnés</strong> à l'issue de la phase d'instruction sont
              transmis à LBP AM aux seules fins de délibération et de sélection finale.
            </P>
            <P>
              Les données des candidats non pré-sélectionnés ne sont pas communiquées à LBP AM. LBP AM s'engage, par
              la signature d'un accord de confidentialité avec Investance Partners, à traiter les informations
              transmises avec la plus haute discrétion et à ne pas les utiliser à des fins autres que celles du Concours.
            </P>
            <P>
              En soumettant leur candidature, les candidats acceptent que les données de leur dossier puissent être
              communiquées aux membres du jury et à LBP AM pour les seuls dossiers pré-sélectionnés, dans les
              conditions définies par la{' '}
              <Link to="/politique-confidentialite" className="ml-link">Politique de confidentialité</Link> de la plateforme.
            </P>
          </Article>

          {/* Article 9 */}
          <Article id={9} title="Propriété intellectuelle">
            <P>
              Les candidats restent propriétaires et responsables de l'ensemble des informations, documents, solutions
              et innovations transmis dans leur dossier de candidature. La transmission du dossier via la plateforme
              <strong> ne confère ni à LBP AM ni à Investance Partners aucun droit de propriété intellectuelle</strong>{' '}
              sur les éléments transmis.
            </P>
            <P>
              Les données de candidature sont utilisées exclusivement aux fins de la sélection des lauréats des
              Trophées de l'innovation 2026 et ne sont en aucun cas utilisées à des fins commerciales.
            </P>
          </Article>

          {/* Article 10 */}
          <Article id={10} title="Protection des données personnelles (RGPD)">
            <P>
              Dans le cadre du Concours, Investance Partners collecte et traite des données à caractère personnel
              conformément au Règlement (UE) 2016/679 (RGPD) et à la loi Informatique et Libertés du 6 janvier 1978
              modifiée. Le responsable de traitement est Investance Partners, 31 rue Saint-Augustin, 75002 Paris.
            </P>
            <P>
              Les données collectées sont traitées pour les finalités suivantes : réception et instruction des
              candidatures, communication relative au Concours, désignation et annonce des lauréats, archivage
              post-concours.
            </P>
            <P>
              <strong>Durées de conservation</strong> : 5 ans à compter de la clôture du Concours pour les dossiers de
              candidature ; 10 ans pour les documents comptables liés aux dotations ; 12 mois pour les données de
              connexion.
            </P>
            <P>
              Les candidats disposent des droits d'accès, de rectification, d'effacement, de limitation, d'opposition
              et de portabilité, exerçables auprès de la référente RGPD :{' '}
              <a href="mailto:rgpd@investance-partners.com" className="ml-link">rgpd@investance-partners.com</a>.
            </P>
            <P>
              Les modalités détaillées figurent dans la{' '}
              <Link to="/politique-confidentialite" className="ml-link">Politique de confidentialité</Link> accessible
              sur la plateforme. Pour toute réclamation, les candidats peuvent saisir la CNIL —{' '}
              <a href="https://www.cnil.fr" target="_blank" rel="noopener noreferrer" className="ml-link">www.cnil.fr</a>.
            </P>
          </Article>

          {/* Article 11 */}
          <Article id={11} title="Obligations du candidat">
            <P>En participant au Concours, le candidat s'engage à :</P>
            <Bullets items={[
              'soumettre un dossier sincère, complet et exact, reflétant fidèlement la réalité de son entreprise et de son innovation ;',
              'ne pas soumettre plusieurs dossiers pour une même entreprise ;',
              "ne pas utiliser la plateforme à d'autres fins que la candidature au Concours ;",
              'respecter les droits de propriété intellectuelle des tiers dans les documents transmis ;',
              "informer sans délai l'Organisateur de tout changement substantiel affectant son éligibilité ;",
              <>respecter les présentes <Link to="/cgu" className="ml-link">CGU</Link> et la <Link to="/politique-confidentialite" className="ml-link">Politique de confidentialité</Link> de la plateforme.</>,
            ]} />
            <P>
              Tout manquement à ces obligations pourra entraîner la <strong>disqualification immédiate</strong> du
              dossier. Avant toute mesure de disqualification, le candidat est informé par e-mail des griefs retenus
              et dispose d'un délai de <strong>sept (7) jours</strong> pour faire valoir ses observations, sauf en cas
              de manquement grave.
            </P>
          </Article>

          {/* Article 12 */}
          <Article id={12} title="Responsabilité de l'Organisateur">
            <P>
              L'Organisateur met en œuvre tous les moyens raisonnables pour assurer le bon fonctionnement de la
              plateforme et le déroulement régulier du Concours. Sa responsabilité ne saurait être engagée en cas
              d'interruption ou d'indisponibilité de la plateforme pour des raisons techniques indépendantes de sa
              volonté.
            </P>
            <P>
              En outre, la responsabilité de l'Organisateur ne saurait être engagée en cas de transmission par le
              candidat d'informations inexactes, incomplètes ou frauduleuses dans son dossier de candidature.
            </P>
            <P>
              En cas de survenance d'un événement de <strong>force majeure</strong> au sens de l'article 1218 du Code
              civil, l'Organisateur se réserve le droit de suspendre ou d'annuler le Concours, sans indemnisation des
              candidats.
            </P>
          </Article>

          {/* Article 13 */}
          <Article id={13} title="Modification et annulation du Concours">
            <P>
              L'Organisateur se réserve le droit de modifier les modalités du présent règlement, de prolonger ou
              d'écourter les délais de candidature, de modifier le calendrier ou, en cas de nécessité absolue,
              d'annuler le Concours. Toute modification substantielle sera portée à la connaissance des candidats
              inscrits par e-mail avec un préavis raisonnable.
            </P>
          </Article>

          {/* Article 14 */}
          <Article id={14} title="Réclamations et médiation">
            <P>
              Pour toute réclamation relative au Concours, les candidats peuvent contacter l'équipe organisatrice via
              la rubrique « Nous contacter » de la plateforme, ou par e-mail à :{' '}
              <a href="mailto:contact.lbpam@investance-partners.com" className="ml-link">contact.lbpam@investance-partners.com</a>.
            </P>
            <P>
              Les parties s'engagent à tenter de résoudre tout litige à l'amiable dans un délai de trente (30) jours.
              En cas d'échec, les parties peuvent recourir au Centre de Médiation et d'Arbitrage de Paris (CMAP —{' '}
              <a href="https://www.cmap.fr" target="_blank" rel="noopener noreferrer" className="ml-link">www.cmap.fr</a>).
            </P>
          </Article>

          {/* Article 15 */}
          <Article id={15} title="Droit applicable et juridiction">
            <P>
              Le présent règlement est régi par le droit français. En cas de litige non résolu à l'amiable, les
              tribunaux de Paris seront seuls compétents.
            </P>
          </Article>

          {/* Récapitulatif (end-of-doc table) */}
          <article style={{
            background: 'white', borderRadius: 14,
            padding: '28px 32px', marginBottom: 18,
            boxShadow: '0 1px 3px rgba(27,30,56,0.06), 0 0 0 1px rgba(27,30,56,0.04)',
          }}>
            <h2 style={{
              fontFamily: 'Lato, sans-serif', fontSize: 20, fontWeight: 700,
              color: NAVY, marginTop: 0, marginBottom: 16,
            }}>Récapitulatif</h2>
            <div style={{ overflowX: 'auto' }}>
              <table style={tableStyle}>
                <tbody>
                  {RECAP_ROWS.map((row, i) => {
                    const value = row[1];
                    const href = row[2];
                    return (
                      <tr key={i} style={{ background: i % 2 ? '#F7F9FC' : 'white' }}>
                        <td style={{ ...tdStyle, fontWeight: 600, color: NAVY, whiteSpace: 'nowrap', width: '38%' }}>{row[0]}</td>
                        <td style={tdStyle}>
                          {href ? (
                            <a href={href} className="ml-link"
                               {...(href.startsWith('http') ? { target: '_blank', rel: 'noopener noreferrer' } : {})}>{value}</a>
                          ) : value}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </article>

          {/* Footer signature */}
          <div style={{
            marginTop: 28, padding: '20px 24px', borderRadius: 10,
            background: 'white', border: '1px solid #E3E7F2',
            color: '#555', fontSize: 13.5, lineHeight: 1.7, textAlign: 'center',
          }}>
            <div>Version : <strong style={{ color: NAVY }}>{VERSION}</strong></div>
            <div style={{ marginTop: 10, fontSize: 12, color: '#888' }}>
              Investance Partners — 31 rue Saint-Augustin, 75002 Paris — RCS Paris 435 257 175
            </div>
          </div>

          {/* CTA — lateral nav between all legal pages */}
          <div style={{ marginTop: 32, textAlign: 'center', display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Link to="/" style={{
              display: 'inline-block', padding: '10px 24px',
              border: `1px solid ${BLUE}`, color: BLUE,
              borderRadius: 999, textDecoration: 'none',
              fontFamily: 'Lato, sans-serif', fontWeight: 600, fontSize: 14,
            }}>← Retour à l'accueil</Link>
            <Link to="/cgu" style={ctaSecondaryStyle}>CGU</Link>
            <Link to="/politique-confidentialite" style={ctaSecondaryStyle}>Politique de confidentialité</Link>
            <Link to="/mentions-legales" style={ctaSecondaryStyle}>Mentions légales</Link>
          </div>
        </div>
      </section>

      <style>{`
        .ml-link {
          color: ${BLUE};
          text-decoration: none;
          border-bottom: 1px solid transparent;
          transition: color 0.15s, border-color 0.15s;
          word-break: break-word;
        }
        .ml-link:hover { color: ${CYAN}; border-bottom-color: ${CYAN}; }
        .ml-link:focus-visible {
          outline: 2px solid ${CYAN};
          outline-offset: 2px;
          border-radius: 2px;
        }
      `}</style>
    </div>
  );
}

// ===== Building blocks (same shape as CGU / Politique pages) =====

function Article({ id, title, children }) {
  return (
    <article style={{
      background: 'white', borderRadius: 14,
      padding: '28px 32px', marginBottom: 18,
      boxShadow: '0 1px 3px rgba(27,30,56,0.06), 0 0 0 1px rgba(27,30,56,0.04)',
    }}>
      <h2 style={{
        fontFamily: 'Lato, sans-serif', fontSize: 18, fontWeight: 700,
        color: NAVY, marginTop: 0, marginBottom: 16,
        display: 'flex', alignItems: 'baseline', gap: 10,
      }}>
        <span style={{
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          padding: '4px 10px', borderRadius: 6,
          background: BLUE, color: 'white', fontSize: 12, fontWeight: 700,
          flexShrink: 0,
        }}>Article {id}</span>
        <span>{title}</span>
      </h2>
      {children}
    </article>
  );
}

function SubTitle({ children }) {
  return (
    <h3 style={{
      fontFamily: 'Lato, sans-serif', fontSize: 15.5, fontWeight: 700,
      color: NAVY, margin: '20px 0 10px',
    }}>{children}</h3>
  );
}

function P({ children, style }) {
  return (
    <p style={{ fontSize: 14.5, lineHeight: 1.7, color: '#333', margin: '0 0 12px', ...style }}>
      {children}
    </p>
  );
}

function Bullets({ items }) {
  return (
    <ul style={{ margin: '6px 0 14px 0', paddingLeft: 22, lineHeight: 1.7, color: '#333', fontSize: 14.5 }}>
      {items.map((it, i) => <li key={i} style={{ marginBottom: 4 }}>{it}</li>)}
    </ul>
  );
}

function Callout({ children, small }) {
  return (
    <div style={{
      background: '#F0F4FB', borderLeft: `4px solid ${BLUE}`,
      padding: small ? '12px 16px' : '14px 18px',
      borderRadius: 8, margin: small ? '12px 0' : '0 0 18px',
      color: NAVY, fontSize: small ? 13.5 : 14, lineHeight: 1.6,
    }}>{children}</div>
  );
}

// ===== Shared styles =====
const navLinkStyle = {
  fontFamily: 'Lato, sans-serif', fontSize: 14, fontWeight: 600,
  padding: '9px 18px', borderRadius: 999, textDecoration: 'none',
  color: 'rgba(255,255,255,0.85)', whiteSpace: 'nowrap',
};

const tableStyle = {
  width: '100%', borderCollapse: 'collapse', fontSize: 13.5,
  border: '1px solid #E3E7F2',
};
const tdStyle = {
  padding: '10px 12px', color: '#333', verticalAlign: 'top',
  borderTop: '1px solid #E3E7F2',
};

const ctaSecondaryStyle = {
  display: 'inline-block', padding: '10px 24px',
  border: `1px solid ${NAVY}`, color: NAVY,
  borderRadius: 999, textDecoration: 'none',
  fontFamily: 'Lato, sans-serif', fontWeight: 600, fontSize: 14,
};
